import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import api from "../../../api";
import CategorySaveFields from "./CategorySaveFields";

const OPTION_COUNTS = [3, 4, 5, 6];

/**
 * Converts an import's already-imported question bank questions into MCQs.
 * Kicks off the job, polls it, then hands off to the MCQ review page.
 *
 * Props:
 *   open, onClose
 *   importId          — question bank import id
 *   paperId           — catalog paper id; used when entered from the Past Papers list,
 *                       where the import behind the paper is resolved server-side
 *   title             — paper name, shown in the dialog
 *   questionCount     — how many questions the paper has (display only)
 *   defaultCategoryId — pre-selects the import's target category
 */
const ConvertToMcqDialog = ({
  open,
  onClose,
  importId,
  paperId,
  title,
  questionCount,
  defaultCategoryId = "",
}) => {
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const [optionsCount, setOptionsCount] = useState(4);
  const [allowMultiSelect, setAllowMultiSelect] = useState(true);
  const [categoryId, setCategoryId] = useState(defaultCategoryId || "");
  const [sourceType, setSourceType] = useState("ai_generated");

  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setCategoryId(defaultCategoryId || "");
  }, [defaultCategoryId, open]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const stopPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/admin/question-bank/mcq-job/${jobId}`);
        const job = res.data?.data;
        setProgress(job?.progress_percent || 0);

        if (job?.status === "preview" || job?.status === "completed") {
          stopPolling();
          setConverting(false);
          onClose?.();
          navigate(`/admin/question-bank/mcq-review?job=${jobId}`);
        } else if (job?.status === "failed") {
          stopPolling();
          setConverting(false);
          setError(job?.error_message || "Conversion failed.");
        }
      } catch (err) {
        stopPolling();
        setConverting(false);
        setError(err.response?.data?.message || "Failed to check conversion status.");
      }
    }, 3000);
  };

  const handleConvert = async () => {
    setConverting(true);
    setError("");
    setProgress(0);

    try {
      const endpoint = importId
        ? `/admin/question-bank/import/${importId}/convert-to-mcq`
        : `/admin/question-bank/paper/${paperId}/convert-to-mcq`;

      const res = await api.post(endpoint, {
        options_count: optionsCount,
        allow_multi_select: allowMultiSelect,
        target_source_type: sourceType,
        target_category_id: categoryId || null,
      });

      const jobId = res.data?.data?.mcq_job_id;
      if (!jobId) throw new Error("No job id returned");
      startPolling(jobId);
    } catch (err) {
      setConverting(false);
      setError(err.response?.data?.message || "Failed to start MCQ conversion.");
    }
  };

  const handleClose = () => {
    if (converting) return;
    setError("");
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Convert to MCQ
      </DialogTitle>

      <DialogContent dividers>
        {title && (
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
            {questionCount ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {questionCount} question{questionCount === 1 ? "" : "s"}
              </Typography>
            ) : null}
          </Typography>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Every question in this paper is turned into a multiple-choice question. The correct
          option is taken from the existing mark scheme &mdash; AI only writes the wrong options.
          Nothing is saved until you review it.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Options per question"
            value={optionsCount}
            onChange={(e) => setOptionsCount(Number(e.target.value))}
            disabled={converting}
            sx={{ minWidth: 180 }}
          >
            {OPTION_COUNTS.map((n) => (
              <MenuItem key={n} value={n}>
                {n} options
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={allowMultiSelect}
                onChange={(e) => setAllowMultiSelect(e.target.checked)}
                disabled={converting}
              />
            }
            label={
              <Typography variant="body2">
                Allow multiple-select when the answer is a set
              </Typography>
            }
          />
        </Box>

        <CategorySaveFields
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          sourceType={sourceType}
          onSourceTypeChange={setSourceType}
          disabled={converting}
          helperText="You can change these again before saving to the question bank."
        />

        {converting && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Converting questions&hellip; {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={converting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConvert}
          disabled={converting}
          startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
        >
          {converting ? "Converting…" : "Convert"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToMcqDialog;
